import { Component, Element, Event, EventEmitter, JSX, Method, Prop, h } from '@stencil/core'

import { TextareaResize } from './air-textarea.types'
import { Validation } from '../../types/status.types'

@Component({
  tag: 'air-textarea',
  styleUrl: 'air-textarea.scss',
  shadow: true,
})
export class AirTextarea {
  /** Reference to the host element */
  @Element() host!: HTMLAirTextareaElement

  /** Specifies textarea label */
  @Prop() label?: string

  /** Defines textarea value */
  @Prop({ mutable: true }) value?: string

  /** Specifies placeholder value */
  @Prop() placeholder?: string

  /** Specifies if textarea should have autofocus */
  // eslint-disable-next-line @stencil-community/reserved-member-names
  @Prop() autofocus = false

  /** Specifies if textarea is read only */
  @Prop() readonly = false

  /** Specifies if textarea is required */
  @Prop() required = false

  /** Specifies if textarea is disabled */
  @Prop() disabled = false

  /** Defines columns */
  @Prop() cols?: number

  /** Defines rows */
  @Prop() rows?: number

  /** Defines maximum characters number */
  @Prop() maxlength?: number

  /** Defines maximum characters number */
  @Prop() resize: TextareaResize = 'vertical'

  /** Specifies validation of textarea */
  @Prop() validation?: Validation

  /** Triggers when user textarea value */
  @Event({ eventName: 'air-input' }) input?: EventEmitter<string>

  /** Triggers when textarea value was changed and blur */
  @Event({ eventName: 'air-change' }) change?: EventEmitter<string>

  /** Triggers when textarea in focus */
  @Event({ eventName: 'air-focus' }) focus?: EventEmitter<string>

  /** Triggers when an textarea has lost focus */
  @Event({ eventName: 'air-blur' }) blur?: EventEmitter<string>

  /** Manually sets focus to the textarea element */
  @Method()
  async setFocus(): Promise<void> {
    this.textareaElement?.focus()
  }

  /** Manually blur textarea element */
  @Method()
  async setBlur(): Promise<void> {
    this.textareaElement?.blur()
  }

  private textareaElement?: HTMLTextAreaElement

  componentDidLoad(): void {
    this.autofocus && this.textareaElement?.focus()
  }

  private handleFocus = (): void => {
    this.focus?.emit(this.value)
  }

  private handleInput = (): void => {
    this.value = this.textareaElement?.value
    this.input?.emit(this.value)
  }

  private handleChange = (): void => {
    this.change?.emit(this.textareaElement?.value)
  }

  private handleBlur = (): void => {
    this.blur?.emit(this.value)
  }

  private renderLabel(): HTMLLabelElement | null {
    return this.label !== undefined ? (
      <label class="textarea-label">
        {this.label}
        {this.required && <span class="required-mark">*</span>}
      </label>
    ) : null
  }

  private renderValidation(): JSX.Element | null {
    return this.validation?.text !== undefined ? (
      <span class="status-text">{this.validation.text}</span>
    ) : null
  }

  render(): JSX.Element {
    const textareaWrapperClasses = {
      'textarea-wrapper': true,
      [`with-status`]: this.validation?.status !== undefined,
      [`${this.validation?.status}`]: this.validation?.status !== undefined,
    }

    const textareaElementClasses = {
      'textarea-element': true,
      [`resize-${this.resize}`]: true,
    }

    const textareaAttributes = {
      class: textareaElementClasses,
      value: this.value,
      cols: this.cols,
      rows: this.rows,
      disabled: this.disabled,
      placeholder: this.placeholder,
      readonly: this.readonly,
      maxlength: this.maxlength,
      onFocus: this.handleFocus,
      onInput: this.handleInput,
      onChange: this.handleChange,
      onBlur: this.handleBlur,
      ref: (el: HTMLTextAreaElement | undefined): void => {
        this.textareaElement = el
      },
    }
    return (
      <div class={textareaWrapperClasses}>
        {this.renderLabel()}
        <textarea {...textareaAttributes}>
          <slot></slot>
        </textarea>
        {this.renderValidation()}
      </div>
    )
  }
}
